import { useState, useEffect } from 'react';
import { useProjects } from '@/context/ProjectContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/StatusBadge';
import { Users, FolderKanban, Plus, Trash2, Edit } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency, formatDate, getPaymentStatus } from '@/utils/helpers';
import { toast } from 'sonner';

export const AdminPanel = () => {
  const { projects, employees, addEmployee, updateEmployee, deleteEmployee } = useProjects();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeFormData, setEmployeeFormData] = useState({
    name: '',
    email: '',
    department: '',
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterPaymentType, setFilterPaymentType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Get unique clients
  const uniqueClients = [...new Set(projects.map((p) => p.clientName))];

  // Apply filters to projects
  const filteredProjects = projects.filter((project) => {
    const matchesEmployee = filterEmployee === 'all' || project.createdBy === filterEmployee;
    const matchesClient = filterClient === 'all' || project.clientName === filterClient;
    const matchesPaymentType = filterPaymentType === 'all' || project.paymentType === filterPaymentType;
    
    let projectStatus = 'active';
    const hasOverdue = project.paymentSchedule.some(
      (p) => getPaymentStatus(p.dueDate, p.status) === 'overdue'
    );
    if (hasOverdue) projectStatus = 'overdue';
    const allPaid = project.paymentSchedule.every((p) => p.status === 'paid');
    if (allPaid) projectStatus = 'completed';
    
    const matchesStatus = filterStatus === 'all' || projectStatus === filterStatus;
    
    return matchesEmployee && matchesClient && matchesPaymentType && matchesStatus;
  });

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setEmployeeFormData({ name: '', email: '', department: '' });
    setIsDialogOpen(true);
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeFormData({
      name: employee.name,
      email: employee.email,
      department: employee.department,
    });
    setIsDialogOpen(true);
  };

  const handleSaveEmployee = async () => {
    if (!employeeFormData.name || !employeeFormData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (selectedEmployee) {
        await updateEmployee(selectedEmployee.id, employeeFormData);
        toast.success('Employee updated successfully');
      } else {
        await addEmployee({ ...employeeFormData, role: 'employee' });
        toast.success('Employee added successfully');
      }
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save employee');
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await deleteEmployee(employeeId);
        toast.success('Employee deleted successfully');
      } catch (error) {
        toast.error('Failed to delete employee');
      }
    }
  };

  const getEmployeeEmail = (project) => {
    // Prefer the email captured on the project at creation time
    if (project.createdByEmail) return project.createdByEmail;
    // Fallback: resolve through the employees list by id
    const emp = employees.find((e) => e.id === project.createdBy);
    return emp?.email || 'Unknown';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Admin Panel</h1>
        <p className="text-muted-foreground mt-1">
          Manage projects, employees, and oversee all payment activities
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-bold mt-2">{projects.length}</p>
              </div>
              <FolderKanban className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Projects</p>
                <p className="text-2xl font-bold mt-2">
                  {projects.filter((p) => p.status === 'active').length}
                </p>
              </div>
              <FolderKanban className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold mt-2">{employees.length}</p>
              </div>
              <Users className="h-8 w-8 text-accent" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Clients</p>
                <p className="text-2xl font-bold mt-2">{uniqueClients.length}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="projects" className="space-y-4">
        <TabsList>
          <TabsTrigger value="projects">All Projects</TabsTrigger>
          <TabsTrigger value="employees">Employees</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid gap-4 md:grid-cols-4">
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by Employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterClient} onValueChange={setFilterClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {uniqueClients.map((client) => (
                      <SelectItem key={client} value={client}>
                        {client}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterPaymentType} onValueChange={setFilterPaymentType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Payment Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="outsourced">Outsourced</SelectItem>
                    <SelectItem value="recurring">Recurring</SelectItem>
                    <SelectItem value="one-time">One-time</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Projects Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Projects ({filteredProjects.length})</CardTitle>
              <CardDescription>Review and manage all project payments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Total Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.map((project) => {
                      const totalAmount = project.totalValue || project.totalAmount || project.paymentSchedule.reduce((sum, p) => sum + p.amount, 0);
                      const hasOverdue = project.paymentSchedule.some(
                        (p) => getPaymentStatus(p.dueDate, p.status) === 'overdue'
                      );
                      const allPaid = project.paymentSchedule.every((p) => p.status === 'paid');

                      return (
                        <TableRow key={project.id}>
                          <TableCell className="font-medium">{project.projectName}</TableCell>
                          <TableCell>{project.clientName}</TableCell>
                          <TableCell>{getEmployeeEmail(project)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {project.paymentType === 'one-time' ? 'One-time' : project.paymentType}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatCurrency(totalAmount)}</TableCell>
                          <TableCell>
                            {hasOverdue && <StatusBadge status="overdue" />}
                            {!hasOverdue && allPaid && <Badge className="status-paid">Completed</Badge>}
                            {!hasOverdue && !allPaid && <Badge>Active</Badge>}
                          </TableCell>
                          <TableCell>
                            <Link to={`/projects/${project.id}`}>
                              <Button variant="ghost" size="sm">
                                View Details
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleAddEmployee} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Employee
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Employees ({employees.length})</CardTitle>
              <CardDescription>Manage employee accounts. Only employees in this list can log in via OTP.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Projects</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((employee) => {
                      const employeeProjects = projects.filter((p) => p.createdBy === employee.id);
                      return (
                        <TableRow key={employee.id}>
                          <TableCell className="font-medium">{employee.name}</TableCell>
                          <TableCell>{employee.email}</TableCell>
                          <TableCell>{employee.department || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{employeeProjects.length}</Badge>
                          </TableCell>
                          <TableCell>{formatDate(employee.createdAt)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditEmployee(employee)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEmployee(employee.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>
              {selectedEmployee ? 'Update employee information' : 'Add a new employee to the system. Once added, they can log in using their email.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emp-name">Name *</Label>
              <Input
                id="emp-name"
                placeholder="John Doe"
                value={employeeFormData.name}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp-email">Email *</Label>
              <Input
                id="emp-email"
                type="email"
                placeholder="john@karvatech.com"
                value={employeeFormData.email}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp-dept">Department</Label>
              <Input
                id="emp-dept"
                placeholder="Development"
                value={employeeFormData.department}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, department: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEmployee}>
              {selectedEmployee ? 'Update' : 'Add'} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};